/**
 * 本地数据源：IndexedDB 存储，免登录可用。报表用 @daynote/core 在本地计算。
 */
import {
  aggregateReport,
  getReportRange,
  planMerge,
  itemDedupKey,
  EXPORT_VERSION,
  type ReportInputItem,
  type DayNoteExport,
  type ExistingSnapshot,
} from '@daynote/core';
import type {
  DayNote,
  Item,
  Group,
  UserSetting,
  ReportType,
} from '@daynote/shared-types';
import { idb } from './idb';
import type { DataSource } from './types';

const LOCAL_USER = 'local';

interface LocalItem {
  id: string;
  date: string;
  content: string;
  note: string | null;
  color: string | null;
  hours: number | null;
  isDone: boolean;
  sortOrder: number;
  groupId: string | null;
  createdAt: string;
  updatedAt: string;
}

const DEFAULT_SETTING: UserSetting = {
  userId: LOCAL_USER,
  theme: 'warm-wood',
  backgroundUrl: null,
  showLunar: true,
  showHoliday: true,
  weekStart: 1,
  maxRows: 5,
};

const now = () => new Date().toISOString();
const uuid = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

function monthRange(month: string): { start: string; end: string } {
  const parts = month.split('-');
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const lastDay = new Date(y, m, 0).getDate();
  return { start: `${month}-01`, end: `${month}-${String(lastDay).padStart(2, '0')}` };
}

function toItem(rec: LocalItem, groupById: Map<string, Group>): Item {
  return {
    id: rec.id,
    dayNoteId: rec.date,
    groupId: rec.groupId,
    group: rec.groupId ? groupById.get(rec.groupId) ?? null : null,
    content: rec.content,
    note: rec.note,
    color: rec.color,
    hours: rec.hours,
    isDone: rec.isDone,
    sortOrder: rec.sortOrder,
    createdAt: rec.createdAt,
    updatedAt: rec.updatedAt,
  };
}

async function groupMap(): Promise<Map<string, Group>> {
  const groups = await idb.getAll<Group>('groups');
  return new Map(groups.map((g) => [g.id, g]));
}

function buildDayNote(date: string, items: Item[]): DayNote {
  const ts = now();
  return {
    id: date,
    userId: LOCAL_USER,
    date,
    items: items.sort((a, b) => a.sortOrder - b.sortOrder),
    createdAt: ts,
    updatedAt: ts,
  };
}

export const localDataSource: DataSource = {
  async getMonth(month) {
    const { start, end } = monthRange(month);
    const [records, gmap] = await Promise.all([
      idb.getItemsByDateRange<LocalItem>(start, end),
      groupMap(),
    ]);
    const byDate = new Map<string, Item[]>();
    for (const rec of records) {
      const arr = byDate.get(rec.date) ?? [];
      arr.push(toItem(rec, gmap));
      byDate.set(rec.date, arr);
    }
    const dayNotes = [...byDate.entries()].map(([date, items]) => buildDayNote(date, items));
    return { dayNotes };
  },

  async getByDate(date) {
    const [records, gmap] = await Promise.all([
      idb.getItemsByDateRange<LocalItem>(date, date),
      groupMap(),
    ]);
    if (records.length === 0) return { dayNote: null };
    const items = records.map((r) => toItem(r, gmap));
    return { dayNote: buildDayNote(date, items) };
  },

  async createItem(date, data) {
    const records = await idb.getItemsByDateRange<LocalItem>(date, date);
    const maxSort = records.reduce((mx, r) => Math.max(mx, r.sortOrder), 0);
    const ts = now();
    const rec: LocalItem = {
      id: uuid(),
      date,
      content: data.content,
      note: data.note ?? null,
      color: data.color ?? null,
      hours: data.hours ?? null,
      isDone: false,
      sortOrder: maxSort + 1,
      groupId: data.groupId ?? null,
      createdAt: ts,
      updatedAt: ts,
    };
    await idb.put('items', rec);
    return toItem(rec, await groupMap());
  },

  async updateItem(itemId, data) {
    const rec = await idb.get<LocalItem>('items', itemId);
    if (!rec) throw new Error('条目不存在');
    const next: LocalItem = {
      ...rec,
      ...(data.content !== undefined && { content: data.content }),
      ...(data.note !== undefined && { note: data.note }),
      ...(data.color !== undefined && { color: data.color }),
      ...(data.isDone !== undefined && { isDone: data.isDone }),
      ...(data.groupId !== undefined && { groupId: data.groupId }),
      ...(data.hours !== undefined && { hours: data.hours }),
      updatedAt: now(),
    };
    await idb.put('items', next);
    return toItem(next, await groupMap());
  },

  async deleteItem(itemId) {
    await idb.delete('items', itemId);
  },

  async updateOrder(_dayNoteId, itemIds) {
    for (let i = 0; i < itemIds.length; i++) {
      const id = itemIds[i];
      if (!id) continue;
      const rec = await idb.get<LocalItem>('items', id);
      if (rec) await idb.put('items', { ...rec, sortOrder: i + 1, updatedAt: now() });
    }
  },

  async listGroups() {
    const groups = await idb.getAll<Group>('groups');
    groups.sort((a, b) => a.sortOrder - b.sortOrder || a.createdAt.localeCompare(b.createdAt));
    return { groups };
  },

  async createGroup(data) {
    const groups = await idb.getAll<Group>('groups');
    if (groups.some((g) => g.abbr.toLowerCase() === data.abbr.toLowerCase())) {
      throw new Error(`简写 [${data.abbr}] 已被占用`);
    }
    const maxSort = groups.reduce((mx, g) => Math.max(mx, g.sortOrder), 0);
    const ts = now();
    const group: Group = {
      id: uuid(),
      userId: LOCAL_USER,
      name: data.name,
      abbr: data.abbr,
      color: data.color ?? null,
      sortOrder: maxSort + 1,
      createdAt: ts,
      updatedAt: ts,
    };
    await idb.put('groups', group);
    return group;
  },

  async updateGroup(id, data) {
    const group = await idb.get<Group>('groups', id);
    if (!group) throw new Error('分组不存在');
    if (data.abbr !== undefined && data.abbr.toLowerCase() !== group.abbr.toLowerCase()) {
      const groups = await idb.getAll<Group>('groups');
      if (groups.some((g) => g.id !== id && g.abbr.toLowerCase() === data.abbr!.toLowerCase())) {
        throw new Error(`简写 [${data.abbr}] 已被占用`);
      }
    }
    const next: Group = {
      ...group,
      ...(data.name !== undefined && { name: data.name }),
      ...(data.abbr !== undefined && { abbr: data.abbr }),
      ...(data.color !== undefined && { color: data.color }),
      ...(data.sortOrder !== undefined && { sortOrder: data.sortOrder }),
      updatedAt: now(),
    };
    await idb.put('groups', next);
    return next;
  },

  async removeGroup(id) {
    await idb.delete('groups', id);
    // onDelete: SetNull —— 关联条目的 groupId 置空，不删条目
    const items = await idb.getAll<LocalItem>('items');
    for (const rec of items) {
      if (rec.groupId === id) await idb.put('items', { ...rec, groupId: null, updatedAt: now() });
    }
  },

  async getReport(type: ReportType, date) {
    const setting = await this.getSetting();
    const { start, end } = getReportRange(type, date, setting.weekStart);
    const [records, groups] = await Promise.all([
      idb.getItemsByDateRange<LocalItem>(start, end),
      idb.getAll<Group>('groups'),
    ]);
    const items: ReportInputItem[] = records
      .slice()
      .sort((a, b) => a.date.localeCompare(b.date) || a.sortOrder - b.sortOrder)
      .map((r) => ({
        id: r.id,
        date: r.date,
        content: r.content,
        hours: r.hours,
        isDone: r.isDone,
        groupId: r.groupId,
      }));
    return aggregateReport({ items, groups, type, start, end });
  },

  async getSetting() {
    const stored = await idb.get<{ key: string; value: UserSetting }>('meta', 'setting');
    return stored?.value ?? DEFAULT_SETTING;
  },

  async updateSetting(patch) {
    const current = await this.getSetting();
    const merged: UserSetting = { ...current, ...patch };
    await idb.put('meta', { key: 'setting', value: merged });
    return merged;
  },

  async exportData(): Promise<DayNoteExport> {
    const [groups, allItems, setting] = await Promise.all([
      idb.getAll<Group>('groups'),
      idb.getAll<LocalItem>('items'),
      this.getSetting(),
    ]);

    groups.sort((a, b) => a.sortOrder - b.sortOrder || a.createdAt.localeCompare(b.createdAt));
    const groupById = new Map(groups.map((g) => [g.id, g]));

    // 按 date 分组 items
    const byDate = new Map<string, LocalItem[]>();
    for (const item of allItems) {
      const arr = byDate.get(item.date) ?? [];
      arr.push(item);
      byDate.set(item.date, arr);
    }

    const dayNotes = [...byDate.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, items]) => ({
        date,
        items: items
          .slice()
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((it, idx) => ({
            content: it.content,
            note: it.note,
            color: it.color,
            hours: it.hours,
            isDone: it.isDone,
            sortOrder: it.sortOrder ?? idx + 1,
            groupAbbr: it.groupId ? (groupById.get(it.groupId)?.abbr ?? null) : null,
          })),
      }));

    // 记录本次导出时间
    await idb.put('meta', { key: 'lastExportAt', value: new Date().toISOString() });

    return {
      app: 'daynote',
      version: EXPORT_VERSION,
      exportedAt: new Date().toISOString(),
      source: 'local',
      data: {
        groups: groups.map((g) => ({
          abbr: g.abbr,
          name: g.name,
          color: g.color,
          sortOrder: g.sortOrder,
        })),
        dayNotes,
        settings: {
          theme: setting.theme ?? undefined,
          showLunar: setting.showLunar,
          showHoliday: setting.showHoliday,
          weekStart: setting.weekStart,
          maxRows: setting.maxRows,
        },
      },
    };
  },

  async importData(payload: DayNoteExport) {
    const [existingGroups, allItems] = await Promise.all([
      idb.getAll<Group>('groups'),
      idb.getAll<LocalItem>('items'),
    ]);
    const gmap = new Map(existingGroups.map((g) => [g.id, g]));

    const snapshot: ExistingSnapshot = {
      groupAbbrs: new Set(existingGroups.map((g) => g.abbr)),
      itemKeys: new Set(
        allItems.map((it) =>
          itemDedupKey(
            it.date,
            it.content,
            it.hours,
            it.groupId ? (gmap.get(it.groupId)?.abbr ?? null) : null,
          ),
        ),
      ),
    };

    const plan = planMerge(payload, snapshot);

    // 执行计划：创建分组
    const abbrToGroupId = new Map(existingGroups.map((g) => [g.abbr, g.id]));
    const maxSort = existingGroups.reduce((mx, g) => Math.max(mx, g.sortOrder), 0);
    let sortCounter = maxSort;
    for (const g of plan.groupsToCreate) {
      sortCounter += 1;
      const ts = now();
      const newGroup: Group = {
        id: uuid(),
        userId: LOCAL_USER,
        name: g.name,
        abbr: g.abbr,
        color: g.color ?? null,
        sortOrder: sortCounter,
        createdAt: ts,
        updatedAt: ts,
      };
      await idb.put('groups', newGroup);
      abbrToGroupId.set(g.abbr, newGroup.id);
    }

    // 执行计划：插入条目
    for (const { date, item } of plan.itemsToInsert) {
      const existing = await idb.getItemsByDateRange<LocalItem>(date, date);
      const maxItemSort = existing.reduce((mx, r) => Math.max(mx, r.sortOrder), 0);
      const ts = now();
      const rec: LocalItem = {
        id: uuid(),
        date,
        content: item.content,
        note: item.note ?? null,
        color: item.color ?? null,
        hours: item.hours ?? null,
        isDone: item.isDone ?? false,
        sortOrder: maxItemSort + 1,
        groupId: item.groupAbbr ? (abbrToGroupId.get(item.groupAbbr) ?? null) : null,
        createdAt: ts,
        updatedAt: ts,
      };
      await idb.put('items', rec);
    }

    return plan.summary;
  },
};
